import type { Modifier } from '@dnd-kit/core';
import type { ScheduleBar } from '../types.js';

/**
 * 316 — the timeline's pure time logic: the instants a view covers, the step a drag snaps to, and the
 * axis ticks. Pure so it can be proven without a browser (jsdom measures every element at 0 px;
 * research R8), and shaped for `dnd-timeline`, whose `Range` is `{ start, end }` in epoch ms.
 *
 * 🔴 Malaysia time is a FIXED +8 h: Malaysia has no daylight saving. Every wall-clock value here is
 * read with UTC getters on an instant shifted by +8 h, never with local-time getters, so the browser's
 * own zone can never move a boundary or a label (the 284 lesson).
 */

export type ScheduleView = 'day' | 'week' | 'month';

export interface TimeRange {
  start: number;
  end: number;
}

export const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function parseDay(day: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) throw new Error(`date must be YYYY-MM-DD, got "${day}"`);
  const midnightUtc = Date.parse(`${day}T00:00:00.000Z`);
  if (Number.isNaN(midnightUtc) || new Date(midnightUtc).toISOString().slice(0, 10) !== day) {
    throw new Error(`date is not a real date: "${day}"`);
  }
  return midnightUtc;
}

/** The MYT calendar date (`YYYY-MM-DD`) of an instant. */
export function mytDateOf(ms: number): string {
  return new Date(ms + MYT_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The instants a view covers, anchored on a MYT date. Mirrors the backend's `scheduleRange`
 * (`schedule/window.ts`): Day is one MYT day, Week runs Monday to Monday, Month the 1st to the 1st.
 */
export function viewRange(view: ScheduleView, date: string): TimeRange {
  const d = parseDay(date);
  let startUtcMidnight: number;
  let endUtcMidnight: number;
  if (view === 'day') {
    startUtcMidnight = d;
    endUtcMidnight = d + DAY_MS;
  } else if (view === 'week') {
    const sinceMonday = (new Date(d).getUTCDay() + 6) % 7;
    startUtcMidnight = d - sinceMonday * DAY_MS;
    endUtcMidnight = startUtcMidnight + 7 * DAY_MS;
  } else {
    const at = new Date(d);
    startUtcMidnight = Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), 1);
    endUtcMidnight = Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 1);
  }
  return { start: startUtcMidnight - MYT_OFFSET_MS, end: endUtcMidnight - MYT_OFFSET_MS };
}

/** What a drag snaps its movement to: 30 min in Day, 1 h in Week, 1 day in Month. */
export function stepFor(view: ScheduleView): number {
  if (view === 'day') return 30 * 60 * 1000;
  if (view === 'week') return HOUR_MS;
  return DAY_MS;
}

export interface Tick {
  at: number;
  label: string;
  /** A stronger line: midnight in Day, every day in Week, Mondays in Month. */
  major: boolean;
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ticksFor(range: TimeRange, view: ScheduleView): Tick[] {
  const unit = view === 'day' ? HOUR_MS : DAY_MS;
  const ticks: Tick[] = [];
  for (let at = range.start; at < range.end; at += unit) {
    const wall = new Date(at + MYT_OFFSET_MS);
    if (view === 'day') {
      const hh = String(wall.getUTCHours()).padStart(2, '0');
      ticks.push({ at, label: `${hh}:00`, major: wall.getUTCHours() === 0 });
    } else if (view === 'week') {
      ticks.push({ at, label: `${WEEKDAY[wall.getUTCDay()]} ${wall.getUTCDate()}`, major: true });
    } else {
      ticks.push({ at, label: String(wall.getUTCDate()), major: wall.getUTCDay() === 1 });
    }
  }
  return ticks;
}

/**
 * #631 — the day columns Week and Month tint: Saturday and Sunday, and today (today wins when it is a
 * weekend day, so there is one tint per column). Day view shows a single day and tints nothing.
 * Malaysia time, like every other date on this screen.
 */
export interface DayBand {
  start: number;
  end: number;
  kind: 'weekend' | 'today';
}

export function dayBands(range: TimeRange, view: ScheduleView, now: number): DayBand[] {
  if (view === 'day') return [];
  const bands: DayBand[] = [];
  for (let at = range.start; at < range.end; at += DAY_MS) {
    const end = Math.min(at + DAY_MS, range.end);
    const weekday = new Date(at + MYT_OFFSET_MS).getUTCDay();
    if (now >= at && now < end) bands.push({ start: at, end, kind: 'today' });
    else if (weekday === 0 || weekday === 6) bands.push({ start: at, end, kind: 'weekend' });
  }
  return bands;
}

/** The now-line is drawn only while now falls inside the view (start inclusive, end exclusive). */
export function nowVisible(now: number, range: TimeRange): boolean {
  return now >= range.start && now < range.end;
}

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `HH:mm` in Malaysia time. */
export function mytClock(ms: number): string {
  const wall = new Date(ms + MYT_OFFSET_MS);
  return `${String(wall.getUTCHours()).padStart(2, '0')}:${String(wall.getUTCMinutes()).padStart(2, '0')}`;
}

/** `Sat 26 Sep` in Malaysia time. */
export function mytDayLabel(ms: number): string {
  const wall = new Date(ms + MYT_OFFSET_MS);
  return `${WEEKDAY[wall.getUTCDay()]} ${wall.getUTCDate()} ${MONTH[wall.getUTCMonth()]}`;
}

/** `Sat 26 Sep, 10:00 – 13:00`, or both days when the period crosses a MYT midnight. */
export function mytPeriod(startMs: number, endMs: number): string {
  const startDay = mytDayLabel(startMs);
  const endDay = mytDayLabel(endMs);
  const start = `${startDay}, ${mytClock(startMs)}`;
  return startDay === endDay
    ? `${start} – ${mytClock(endMs)}`
    : `${start} – ${endDay}, ${mytClock(endMs)}`;
}

// --- 316 T018: how a drag becomes one proposal (US1) ---

/** `row` = another car (vertical), `time` = the same car, other times (horizontal). */
export type Axis = 'row' | 'time';
/** 328 (#736): a drag's axis right now — one of the two, or `free` (both at once, a diagonal move). */
export type DragAxis = Axis | 'free';

export interface DragAbilities {
  /** `manage Booking` — the gate on dragging any booking (FR-006/007/008). */
  canMoveBooking: boolean;
  /** `update` + `create CarBlock` — block editing keeps its existing permission (FR-015). */
  canEditBlocks: boolean;
}

/**
 * 328 (#736): a booking that may move either way gives ONE `move` — a target car (possibly its own)
 * and a time shift (possibly none), saved in one server step. `car` stays for row-only bookings
 * (checked out / in), which keep today's Change Car / Correct Car paths.
 */
export type DragProposal =
  | { kind: 'move'; bar: ScheduleBar; targetCarId: string; deltaMs: number }
  | { kind: 'car'; bar: ScheduleBar; targetCarId: string }
  | { kind: 'block-move'; bar: ScheduleBar; deltaMs: number };

/** How far the pointer must travel before a press becomes a drag (research R4). */
export const DRAG_THRESHOLD_PX = 4;

const TIME_MOVABLE = new Set(['awaiting_payment', 'confirmed']);
const ROW_ONLY = new Set(['checked_out', 'checked_in']);
// 379: a finished, paid rental moves either way as a correction of its record (the server asks for the reason).
const OVER = new Set(['completed', 'checked_in']);

/**
 * What a bar may do. A block moves in time on its car. A booking needs `manage Booking` and a real
 * booking behind it: awaiting payment and confirmed move either way; checked out and checked in move
 * between cars only (FR-009). A legacy extension hold belongs to its rental and is not dragged on its
 * own. Anything else stays put.
 * 328 (#736, US3): any booking leg of an extended rental moves either way and takes the WHOLE rental
 * with it (the server shifts every live leg together) — superseding 316 FR-010 and 327's guard.
 */
export function allowedAxes(
  bar: ScheduleBar,
  abilities: DragAbilities,
  now: number = Date.now(),
): Axis[] {
  if (bar.kind !== 'booking' && bar.kind !== 'extension') {
    return abilities.canEditBlocks ? ['time'] : [];
  }
  if (!abilities.canMoveBooking || bar.kind === 'extension' || !bar.booking_id) return [];
  const status = bar.booking_status ?? '';
  // 379: over (its end has passed) and paid — a correction, car and time. The server decides on the whole rental.
  if (OVER.has(status) && bar.money_settled && new Date(bar.end_at).getTime() <= now) {
    return ['row', 'time'];
  }
  if (ROW_ONLY.has(status)) return ['row'];
  if (TIME_MOVABLE.has(status)) return ['row', 'time'];
  return [];
}

/**
 * Snap the DISTANCE moved to the step, never the times themselves (research R3): a 10:15–12:40
 * booking moved two 30-minute steps is 11:15–13:40, length and minutes kept. `+ 0` turns -0 into 0.
 */
export function snapDelta(deltaMs: number, step: number): number {
  return Math.round(deltaMs / step) * step + 0;
}

/** A span moved by a delta (a car change passes 0 and keeps the span). */
export function spanFromDrag(span: TimeRange, deltaMs: number): TimeRange {
  return { start: span.start + deltaMs, end: span.end + deltaMs };
}

/** Which axis a movement is on: nothing below the threshold, then whichever went further. */
export function axisLock(dx: number, dy: number, threshold = DRAG_THRESHOLD_PX): Axis | null {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < threshold) return null;
  return Math.abs(dx) >= Math.abs(dy) ? 'time' : 'row';
}

/**
 * The axis a drag of this bar is on right now, limited to what the bar may do: nothing under the
 * threshold; a one-way bar's only axis; and, since 328, `free` for a bar allowed both ways (316's
 * one-axis lock for these is superseded by #736: a diagonal drag moves the car and the time together).
 */
export function lockedAxis(dx: number, dy: number, axes: Axis[]): DragAxis | null {
  if (axes.length === 0) return null;
  if (axisLock(dx, dy) === null) return null;
  if (axes.length === 1) return axes[0] ?? null;
  return 'free';
}

/**
 * A dnd-kit modifier, recomputed live so the overlay shows the change being made: a one-way bar moves
 * along its axis only; a both-ways bar moves freely (328); under the threshold nothing moves.
 */
export function axisLockModifier(axes: Axis[]): Modifier {
  return ({ transform }) => {
    const axis = lockedAxis(transform.x, transform.y, axes);
    if (axis === 'free') return transform;
    if (axis === 'time') return { ...transform, y: 0 };
    if (axis === 'row') return { ...transform, x: 0 };
    return { ...transform, x: 0, y: 0 };
  };
}

export interface DropInput {
  bar: ScheduleBar;
  view: ScheduleView;
  abilities: DragAbilities;
  /** The horizontal movement, converted to time. */
  deltaMs: number;
  /** The axis at release (`lockedAxis`), or null when the pointer never passed the threshold. */
  axis: DragAxis | null;
  /** The car row under the pointer at release. */
  overCarId: string | null;
  range: TimeRange;
  /** 328: the span that moves (a whole rental), for the edge clamp; the bar's own when absent. */
  span?: TimeRange;
}

/** The time shift a drag asks for, snapped, and stopped at the view's edges in whole steps. */
function clampedDelta(input: DropInput): number {
  const step = stepFor(input.view);
  const start = input.span?.start ?? Date.parse(input.bar.start_at);
  const end = input.span?.end ?? Date.parse(input.bar.end_at);
  const lower = start < input.range.start ? 0 : input.range.start - start;
  const upper = end > input.range.end ? 0 : input.range.end - end;
  const clamped = Math.min(Math.max(snapDelta(input.deltaMs, step), lower), upper);
  return Math.trunc(clamped / step) * step + 0;
}

/**
 * Exactly one proposal, or nothing (FR-003). A drop on the starting place gives nothing. A time move
 * stops at the view's edge in whole steps, so it never silently leaves the visible period; a bar
 * already past an edge cannot be pushed further past it.
 */
export function resolveDrop(input: DropInput): DragProposal | null {
  const { bar, axis } = input;
  const axes = allowedAxes(bar, input.abilities);
  if (axis === null) return null;
  const isBooking = bar.kind === 'booking' || bar.kind === 'extension';

  // 328: both ways at once — the car under the pointer (its own when released outside every row) and
  // the time shift, in ONE proposal. A drop on the starting place is still nothing.
  if (axis === 'free') {
    if (!isBooking || !axes.includes('row') || !axes.includes('time')) return null;
    const targetCarId = input.overCarId ?? bar.car_id;
    const deltaMs = clampedDelta(input);
    if (targetCarId === bar.car_id && deltaMs === 0) return null;
    return { kind: 'move', bar, targetCarId, deltaMs };
  }

  if (!axes.includes(axis)) return null;

  if (axis === 'row') {
    if (!input.overCarId || input.overCarId === bar.car_id) return null;
    return { kind: 'car', bar, targetCarId: input.overCarId };
  }

  const deltaMs = clampedDelta(input);
  if (deltaMs === 0) return null;
  return isBooking
    ? { kind: 'move', bar, targetCarId: bar.car_id, deltaMs }
    : { kind: 'block-move', bar, deltaMs };
}

// --- 316 T022/T024: resize and create (US4, US5) ---

export type ResizeProposal = { kind: 'resize'; bar: ScheduleBar; start: number; end: number };
export type CreateProposal = { kind: 'create'; carId: string; start: number; end: number };

/**
 * Whether a bar gets resize handles. Never in Month (FR-014). A block, behind the block gate. A
 * booking only when it is UNPAID for certain (`money_settled === false`; an unknown state is not
 * unpaid), awaiting payment or confirmed, with `manage Booking`, and neither an extension leg nor a
 * legacy extension hold (decision A, FR-016): a resize reprices, and a paid total cannot change here.
 */
export function canResize(bar: ScheduleBar, abilities: DragAbilities, view: ScheduleView): boolean {
  if (view === 'month') return false;
  if (bar.kind !== 'booking' && bar.kind !== 'extension') return abilities.canEditBlocks;
  return (
    abilities.canMoveBooking &&
    bar.kind === 'booking' &&
    Boolean(bar.booking_id) &&
    !bar.root_booking_id &&
    !bar.has_extension &&
    bar.money_settled === false &&
    TIME_MOVABLE.has(bar.booking_status ?? '')
  );
}

export interface ResizeInput {
  bar: ScheduleBar;
  view: ScheduleView;
  abilities: DragAbilities;
  direction: 'start' | 'end';
  deltaMs: number;
  range: TimeRange;
}

/** One edge moved by whole steps, clamped to the view and to at least one step long. */
export function resolveResize(input: ResizeInput): ResizeProposal | null {
  const { bar, view, range } = input;
  if (!canResize(bar, input.abilities, view)) return null;
  const step = stepFor(view);
  const start = Date.parse(bar.start_at);
  const end = Date.parse(bar.end_at);
  const delta = snapDelta(input.deltaMs, step);
  let nextStart = start;
  let nextEnd = end;
  if (input.direction === 'start') {
    if (start < range.start) return null; // that edge is off screen
    nextStart = Math.min(Math.max(start + delta, range.start), end - step);
  } else {
    if (end > range.end) return null;
    nextEnd = Math.max(Math.min(end + delta, range.end), start + step);
  }
  if (nextStart === start && nextEnd === end) return null;
  return { kind: 'resize', bar, start: nextStart, end: nextEnd };
}

export interface CreateInput {
  carId: string;
  view: ScheduleView;
  range: TimeRange;
  canEditBlocks: boolean;
  fromMs: number;
  toMs: number;
}

/**
 * A block drawn on a car: the earlier end floored and the later end ceiled to the step (counted from
 * the view's MYT start, so Month draws whole days), at least one step long, inside the view.
 */
export function resolveCreate(input: CreateInput): CreateProposal | null {
  if (!input.canEditBlocks) return null;
  const { range } = input;
  const step = stepFor(input.view);
  const lo = Math.min(input.fromMs, input.toMs) - range.start;
  const hi = Math.max(input.fromMs, input.toMs) - range.start;
  let start = range.start + Math.floor(lo / step) * step;
  let end = range.start + Math.ceil(hi / step) * step;
  if (end <= start) end = start + step;
  start = Math.max(start, range.start);
  end = Math.min(end, range.end);
  if (end <= start) return null;
  return { kind: 'create', carId: input.carId, start, end };
}

// --- 328 (#736, FR-006): the estimate while dragging ---

/** A rental's key: its original booking (325), the booking itself when never extended. */
export function rentalKeyOf(bar: ScheduleBar): string | null {
  return bar.root_booking_id ?? bar.booking_id ?? null;
}

/**
 * What occupies `carId` in the loaded schedule, the moving rental's own bars excluded. The page passes
 * the UNFILTERED bars, so a booking the status filter hides still counts. A bar that holds nothing
 * (`unheld`) occupies nothing, as `classifyBar` says.
 */
export function busyFor(
  carId: string,
  bars: readonly ScheduleBar[],
  excludeRental: string | null,
): Array<{ start: number; end: number }> {
  return bars
    .filter(
      (b) =>
        b.car_id === carId &&
        b.state !== 'unheld' &&
        (excludeRental === null || rentalKeyOf(b) !== excludeRental),
    )
    .map((b) => ({ start: Date.parse(b.start_at), end: Date.parse(b.end_at) }));
}

/** Whether a span overlaps anything busy (half-open: back to back is free). */
export function estimateClash(
  span: TimeRange,
  busy: ReadonlyArray<{ start: number; end: number }>,
): boolean {
  return busy.some((b) => b.start < span.end && b.end > span.start);
}

/**
 * The nearest free start for a span of `length`, estimated from the loaded schedule: the SAME rule as
 * the server's `nearestFreeStart` (backend `bookings/moveSuggestion.ts`) without operating hours, which
 * the page does not know. Candidates are the view's grid from the drop point plus, in Day and Week, the
 * back-to-back points of every busy interval; inside the window, not before now, nearest to the drop,
 * the later one on a tie.
 */
export function estimateSuggestion(input: {
  dropStart: number;
  length: number;
  window: TimeRange;
  view: ScheduleView;
  now: number;
  busy: ReadonlyArray<{ start: number; end: number }>;
}): number | null {
  const { dropStart, length, window, view, now, busy } = input;
  const step = stepFor(view);
  const latest = window.end - length;
  if (latest < window.start) return null;
  const candidates = new Set<number>();
  for (
    let k = Math.ceil((window.start - dropStart) / step);
    k <= Math.floor((latest - dropStart) / step);
    k += 1
  ) {
    candidates.add(dropStart + k * step);
  }
  if (view !== 'month') {
    for (const b of busy) {
      candidates.add(b.end);
      candidates.add(b.start - length);
    }
  }
  let best: number | null = null;
  for (const c of candidates) {
    const end = c + length;
    if (c < window.start || end > window.end || c < now) continue;
    if (busy.some((b) => b.start < end && b.end > c)) continue;
    if (best === null) {
      best = c;
      continue;
    }
    const d = Math.abs(c - dropStart);
    const bd = Math.abs(best - dropStart);
    if (d < bd || (d === bd && c > best)) best = c;
  }
  return best;
}

/**
 * 328 (US3): the span a drag moves — the whole rental (325's key) as far as the loaded bars show it on
 * the dragged bar's car; a booking never extended is its own span. The server's preview gives the exact
 * span on confirm.
 */
export function movingSpan(bar: ScheduleBar, bars: readonly ScheduleBar[]): TimeRange {
  const key = rentalKeyOf(bar);
  let start = Date.parse(bar.start_at);
  let end = Date.parse(bar.end_at);
  if (key === null) return { start, end };
  for (const b of bars) {
    if (b.car_id !== bar.car_id || rentalKeyOf(b) !== key) continue;
    start = Math.min(start, Date.parse(b.start_at));
    end = Math.max(end, Date.parse(b.end_at));
  }
  return { start, end };
}

export interface MoveEstimate {
  /** The loaded schedule shows the target taken. */
  clash: boolean;
  /** Where the suggested free time would be (the outline), when it clashes and one exists. */
  suggestion: TimeRange | null;
}

/**
 * 328 (#736, FR-006): what the drag preview shows for a proposal — an estimate from the loaded bars
 * (unfiltered; the moving rental excluded), never a verdict: the server's check on confirm is final.
 */
export function moveEstimate(input: {
  proposal: DragProposal | null;
  bars: readonly ScheduleBar[];
  view: ScheduleView;
  range: TimeRange;
  now: number;
}): MoveEstimate | null {
  const { proposal } = input;
  if (!proposal || proposal.kind !== 'move') return null;
  const span = movingSpan(proposal.bar, input.bars);
  const length = span.end - span.start;
  const target = { start: span.start + proposal.deltaMs, end: span.end + proposal.deltaMs };
  const busy = busyFor(proposal.targetCarId, input.bars, rentalKeyOf(proposal.bar));
  if (!estimateClash(target, busy)) return { clash: false, suggestion: null };
  const start = estimateSuggestion({
    dropStart: target.start,
    length,
    window: input.range,
    view: input.view,
    now: input.now,
    busy,
  });
  return { clash: true, suggestion: start === null ? null : { start, end: start + length } };
}
